import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';

import { HisNotificationService, HisUiService, HisNavigationService, GenericValue } from 'hisng';

import { DataDictionaryUtils } from 'app/fs/res/common/utils/data-dictionary-utils';
import { PublicationService } from '../../../service/publication.service';
import { RightsUtils } from 'app/fs/res/common/utils/rights-utils';
import { GlobalIdentifierEnum, GlobalIdentifierType } from '../../../model/enums/global-identifier-enum';
import { GlobalIdentifier } from '../../../model/global-identifier';
import { Publication } from '../../../model/publication';

@Component({
    selector: 'res-publication-view-details',
    templateUrl: './publication-view-details.component.html',
})
export class PublicationViewDetailsComponent implements OnInit {
    public publication: Publication = new Publication();
    private selectedPublicationId: number;

    public identifierEnum: typeof GlobalIdentifierEnum = GlobalIdentifierEnum; // used in html
    public showEditButton = true;
    public hasEditRights = false;
    public hasChangeRequestRights = false;
    public showBackButton = false;
    public showSearchButton = true;
    public showParentLink = true;
    public showCitationDialog = false;
    public isDuplicate = false;
    public prevPage: string;
    public dateFormat: string;
    public personRights: Map<number, boolean> = new Map<number, boolean>();

    constructor( private route: ActivatedRoute,
        private router: Router,
        public rights: RightsUtils,
        public hisUiService: HisUiService,
        private notificationService: HisNotificationService,
        private navigationService: HisNavigationService,
        private publicationService: PublicationService ) {
    }

    ngOnInit() {
        this.route.params.subscribe( ( paramMap: Params)  => {
            this.selectedPublicationId = parseInt(paramMap.publicationId, 10);
            this.publicationService.canEditPublication(this.selectedPublicationId).subscribe(
                ( hasEditRights: boolean ) => this.hasEditRights = hasEditRights );
            this.publicationService.canRequestChange(this.selectedPublicationId).subscribe(
                ( hasChangeRequestRights: boolean ) => this.hasChangeRequestRights = hasChangeRequestRights );
            this.initView();
        });
        // read parameters - back button and edit button
        this.route.queryParams.subscribe( ( queryParams: Params ) => {
            this.showEditButton = queryParams.showEditButton ? queryParams.showEditButton === 'true' : this.showEditButton;
            this.showBackButton = queryParams.showBackButton ? queryParams.showBackButton === 'true' : this.showBackButton;
            this.showSearchButton = queryParams.showSearchButton ? queryParams.showSearchButton === 'true' : this.showSearchButton;
            if ( queryParams.forValidation === 'true' ) {
                this.notificationService.successI18nRes(`fs.res.publication.success.${queryParams.msg}`);
            }
            this.isDuplicate = queryParams.isDuplicate ? queryParams.isDuplicate === 'true' : this.isDuplicate;
            this.prevPage = queryParams.prevPage;
        });
    }

    /*
     * Method to initialize the detail view of publication.
     * Reads the publication from the database and if available the parent publication as well.
     * Necessary to allow reload of view if publication id is changed. (e.g. when calling detail view of parent)
     */
    private initView() {
        this.publicationService.getPublicationWithDetailsById(this.selectedPublicationId).subscribe(
            ( publication: Publication ) => {
                this.publication = publication;
                console.log('Init view publication: ' , this.publication);
                if (this.publication.parent) {
                    this.publicationService.canViewPublication(this.publication.parent.id).subscribe(
                        ( hasRightViewPublication: boolean ) => this.showParentLink = hasRightViewPublication
                    );
                }
            } );
    }

    // return the url of the current window
    public getUrl() {
        return window.location.href;
    }

    /**
     * call edit-view of publication
     */
    public edit() {
        this.navigationService.setLastBackUrl(window.location.href); // remember last back
        if (this.hasEditRights) {
            this.router.navigate([`/pub/edit/${this.publication.id}`], { queryParams: { showSearchButton: true, showBackButton: true } });
        } else if (this.hasChangeRequestRights){
            this.router.navigate(
                [`/pub/change-request/${this.publication.id}`], { queryParams: { showSearchButton: true, showBackButton: true } }
            );
        }
    }

    /* get all containing project Ids for searchConfiguration searchEntityIds */
    public getProjectIdsAsString(): string {
        if (this.publication.projects) {
            return this.publication.projects.map(p => p.id).join(',');
        }
        return undefined;
    }

    public getDdKey(source: string) {
        if ( this.publication && this.publication.publicationType ) {
            return DataDictionaryUtils.dDKeyPublicationType(this.publication.publicationType, source);
        }
        return undefined;
    }

    public isEmpty( input: any ): boolean {
        return !input || !input.length;
    }

    public isAnalogIdentifier(identifier: GlobalIdentifier): boolean {
        return identifier && GlobalIdentifierType.analogIdentifiers().findIndex(c =>
            c === identifier.globalIdentifierType.hiskeyId) > -1;
    }

    public findIdentifierByHiskeyId(hiskeyId: number): boolean {
        return this.publication.globalIdentifiers.findIndex(identifier =>
            identifier.globalIdentifierType.hiskeyId === hiskeyId) > -1;
    }

    public getGenericValueIdForQueryParams(genericValue: GenericValue) {
        return '[\"' + genericValue.id + '\"]';
    }

    public getResearchAreaKdsfQueryParams(researchAreaKdsf: GenericValue): any {
        let queryParams = this.getBaseSearchQueryParams();
        /* eslint-disable-next-line @typescript-eslint/naming-convention, max-len */
        queryParams['Publication.researchAreas{researchAreaType="kdsf"}.id[]'] = this.getGenericValueIdForQueryParams(researchAreaKdsf);
        return queryParams;
    }

    public getResearchAreaQueryParams(researchArea: GenericValue): any {
        let queryParams = this.getBaseSearchQueryParams();
        /* eslint-disable-next-line @typescript-eslint/naming-convention, max-len */
        queryParams['Publication.researchAreas{researchAreaType="hochschuleigen"}.id[]'] = this.getGenericValueIdForQueryParams(researchArea);
        return queryParams;
    }

    private getBaseSearchQueryParams(): any {
        return {
            configFile: 'fs/res/searchPublicationHeadless.xml',
            pageTitle: 'fs.res.publication.search.data',
            tableTitle: 'fs.res.publication.search.found',
            extended: true,
            showBackButton:true,
            isNotActiveSearch:true
        };
    }

    public onBeforeNavigate() {
        this.navigationService.setLastBackUrl(window.location.href);
    }

    back() {
        this.navigationService.navigateToLastBackUrl();
    }
}
